import { Icon12Cancel, Icon20Cancel } from '@vkontakte/icons';
import { IconButton } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { animated, config, useSpringRef, useTransition } from 'react-spring';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';

import styles from './AsidePollModal.css';
import type { AsidePollProps, PollResult, PollStep } from './AsidePollModal.types';
import { Intro } from './Intro/Intro';
import { Poll } from './Poll/Poll';
import { Result } from './Result/Result';

export const AsidePollModal = memo<AsidePollProps>(
    ({
        introTitle,
        introText,
        introImg,
        introBtnText,
        pollQuestions,
        displayResult,
        analyticsPrefix,
        onShow,
        onClose,
        showAboveViewer = false,
        allowBackButton = false,
    }) => {
        const [step, setStep] = useState<PollStep>('intro');
        const [results, setResults] = useState<PollResult[]>([]);

        const isPollStep = typeof step === 'number';

        const transRef = useSpringRef();
        const transitions = useTransition(step, {
            ref: transRef,
            keys: null,
            from: { opacity: 0 },
            enter: { opacity: 1 },
            leave: { opacity: 0.5 },
            config: {
                ...config.slow,
                duration: 250,
            },
        });

        useEffect(() => {
            onShow?.();
            emitAnalyticEvent(AnalyticEventNames.ASIDE_POLL_SHOW);
        }, []);

        const handleClose = useCallback(() => {
            onClose?.();
        }, [onClose]);

        useEffect(() => {
            transRef.start();
        }, [step]);

        return (
            <>
                {transitions((style) => {
                    return (
                        <div
                            className={classNames(styles.root, {
                                [styles.root_showAboveViewer]: showAboveViewer,
                            })}
                        >
                            <animated.div style={style}>
                                <IconButton
                                    label="Закрыть"
                                    className={classNames(styles.close, {
                                        [styles.close_overImage]: !isPollStep,
                                    })}
                                    onClick={handleClose}
                                >
                                    {!isPollStep ? <Icon12Cancel /> : <Icon20Cancel />}
                                </IconButton>
                                {step === 'intro' && (
                                    <Intro
                                        introTitle={introTitle}
                                        introText={introText}
                                        introImg={introImg}
                                        introBtnText={introBtnText}
                                        setStep={setStep}
                                        analyticsPrefix={analyticsPrefix}
                                    />
                                )}
                                {isPollStep && (
                                    <Poll
                                        pollQuestions={pollQuestions}
                                        step={step}
                                        setStep={setStep}
                                        results={results}
                                        setResults={setResults}
                                        allowBackButton={allowBackButton}
                                        analyticsPrefix={analyticsPrefix}
                                    />
                                )}
                                {step === 'result' && <Result results={results} displayResult={displayResult} onClose={onClose} />}
                            </animated.div>
                        </div>
                    );
                })}
            </>
        );
    }
);

AsidePollModal.displayName = 'AsidePollModal';
